import { Button, Card } from '@payaca/ambrosia';
import { FC, useMemo, type JSX } from 'react';
import { useIntercom } from 'react-use-intercom';

import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';
import LoqateAdvisoryEmailInputField from '../loqateAdvisoryEmailInputField/LoqateAdvisoryEmailInputField';

import { parseCustomerAddress } from '@/helpers/customerHelper';
import { useBusinessAccountForm } from './useBusinessAccountForm';
import { useCompanyInfoFormValidators } from '@/hooks/useCompanyInfoValidators';
import SettingsSaveButton from './SettingsSaveButton';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';
import { getRegionalTextString } from '@payaca/helpers/internationalHelper';
import { RegionalStrings } from '@payaca/types/internationalTypes';

type FormState = {
  businessName: string;
  email: string;
  contactNumber: string;
  firstLineAddress: string;
  secondLineAddress: string;
  city: string;
  postcode: string;
  registeredCountryName: string;
  companyRegistrationNumber: string;
};

type Props = {
  readOnly: boolean;
};

const CompanySettingsBusinessDetails: FC<Props> = ({
  readOnly,
}: Props): JSX.Element => {
  const { show } = useIntercom();
  const account = useAccount();
  const region = useRegion();

  const postcodeRegionalString = useMemo(
    () => getRegionalTextString(region, RegionalStrings.POSTCODE),
    [region]
  );

  const { firstLineAddress, secondLineAddress } = parseCustomerAddress(
    account.address
  );

  const initialState: FormState = {
    businessName: account.companyName ?? '',
    email: account.email ?? '',
    contactNumber: account.contactNumber ?? '',
    firstLineAddress: firstLineAddress ?? '',
    secondLineAddress: secondLineAddress ?? '',
    city: account.city ?? '',
    postcode: account.postcode ?? '',
    registeredCountryName: account.registeredCountryName ?? '',
    companyRegistrationNumber: account.companyRegistrationNumber ?? '',
  };

  const fieldValidators = useCompanyInfoFormValidators([]);

  const {
    formState,
    updateFormFields,
    formValidationResult,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, fieldValidators);

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-2">
        <div>
          <span>
            Your registered business name is stored as{' '}
            <strong>{account.legalBusinessName}</strong>. If this is incorrect
            or you wish to change it,{' '}
            <Button variant="link" onClick={show}>
              please contact us directly
            </Button>
            .
          </span>
        </div>
        <ValidatedFieldWrapper
          validationResult={formValidationResult.businessName}
        >
          <BasicField
            name="businessName"
            label="Trading name"
            value={formState.businessName}
            isRequired={true}
            styleVariant={InputStyleVariant.OUTSIZE}
            onChange={updateFormFields}
            isDisabled={readOnly}
          />
        </ValidatedFieldWrapper>
        <ValidatedFieldWrapper validationResult={formValidationResult.email}>
          <LoqateAdvisoryEmailInputField
            name="email"
            label={'Email address'}
            value={formState.email}
            styleVariant={InputStyleVariant.OUTSIZE}
            onChange={updateFormFields}
            isDisabled={readOnly}
          />
        </ValidatedFieldWrapper>
        <ValidatedFieldWrapper
          validationResult={formValidationResult.contactNumber}
        >
          <BasicField
            name="contactNumber"
            label={'Telephone number'}
            value={formState.contactNumber}
            styleVariant={InputStyleVariant.OUTSIZE}
            onChange={updateFormFields}
            isDisabled={readOnly}
          />
        </ValidatedFieldWrapper>
        <BasicField
          name="firstLineAddress"
          label="Address line 1"
          value={formState.firstLineAddress}
          styleVariant={InputStyleVariant.OUTSIZE}
          onChange={updateFormFields}
          isDisabled={readOnly}
        />
        <BasicField
          name="secondLineAddress"
          label="Address line 2"
          value={formState.secondLineAddress}
          styleVariant={InputStyleVariant.OUTSIZE}
          onChange={updateFormFields}
          isDisabled={readOnly}
        />
        <BasicField
          name="city"
          label="Town"
          value={formState.city}
          styleVariant={InputStyleVariant.OUTSIZE}
          onChange={updateFormFields}
          isDisabled={readOnly}
        />
        <BasicField
          name="postcode"
          label={postcodeRegionalString}
          value={formState.postcode}
          styleVariant={InputStyleVariant.OUTSIZE}
          onChange={updateFormFields}
          isDisabled={readOnly}
        />
        <BasicField
          name="registeredCountryName"
          label="Registered country name"
          value={formState.registeredCountryName ?? ''}
          styleVariant={InputStyleVariant.OUTSIZE}
          onChange={updateFormFields}
          isDisabled={readOnly}
        />
        <BasicField
          name="companyRegistrationNumber"
          label="Company registration number"
          value={formState.companyRegistrationNumber ?? ''}
          styleVariant={InputStyleVariant.OUTSIZE}
          onChange={updateFormFields}
          isDisabled={readOnly}
        />
        {!readOnly && (
          <SettingsSaveButton
            isDirty={isDirty}
            isSaving={isSaving}
            isValid={isValid}
            onSave={saveAndFlush}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsBusinessDetails;
